import { FC, MouseEvent, useState } from "react";
import {
  Box,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography
} from "@mui/material";
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import LogoutIcon from "@mui/icons-material/Logout";
import { useMsal } from "@azure/msal-react";
import { useThemeMode } from "../../theme";
import { ThemeSwitch } from "../../components/themeSwitch";
import GitInfo from "../../generatedGitInfo.json";

/**
 * The phone topbar's single account button. Everything the desktop topbar lays out side by side
 * (name, email, theme switch, commit hash, sign out) lives in this one menu instead.
 */
export const MobileAccountMenu: FC = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { accounts, instance } = useMsal();
  const { mode, toggleTheme } = useThemeMode();
  const profile = instance.getActiveAccount() || accounts[0];
  const open = Boolean(anchorEl);

  return (
    <>
      <IconButton
        aria-label="Account"
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        onClick={(e: MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget)}
        edge="end"
      >
        <AccountCircleOutlinedIcon
          sx={{
            color: (t) => t.palette.MFT_OFF_WHITE
          }}
        />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Box sx={{ paddingX: 2, paddingY: 1, maxWidth: 280 }}>
          <Typography variant="subtitle2" noWrap>
            {profile?.name || profile?.username || "User"}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            noWrap
            sx={{ display: "block" }}
          >
            {profile?.username || ""}
          </Typography>
        </Box>
        <Divider />
        <MenuItem onClick={toggleTheme}>
          <ListItemText primary="Dark mode" />
          <ThemeSwitch
            checked={mode === "dark"}
            // The row's own click toggles, so the switch is display only here.
            tabIndex={-1}
            sx={{ pointerEvents: "none", marginLeft: 1 }}
          />
        </MenuItem>
        <MenuItem onClick={() => instance.logoutRedirect()}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Sign out" />
        </MenuItem>
        {GitInfo?.gitCommitHash && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", paddingX: 2, paddingTop: 1 }}
          >
            commit: {GitInfo.gitCommitHash}
          </Typography>
        )}
      </Menu>
    </>
  );
};
